/**
 * Typical private-office prices per seat around a location, as served by the
 * location service's `/office-price-benchmark`. Mirrors the response there; the
 * ranges themselves are built nightly by the job service.
 */

/** The lead's capacity ranges (`desiredCapacity`), which ranges are kept by. */
export const officeCapacityRanges = [
    "CAPACITY_1_3",
    "CAPACITY_3_6",
    "CAPACITY_6_10",
    "CAPACITY_10_20",
    "CAPACITY_20_PLUS",
] as const;
export type TOfficeCapacityRange = (typeof officeCapacityRanges)[number];

export interface OfficePriceBenchmark {
    /** Where the range comes from: the most specific level with enough data. */
    level: "CITY" | "REGION" | "COUNTRY";
    areaName: string;
    /** True when the area had too few offices of this size, so the range is
     *  over every size there. */
    allSizes: boolean;
    offices: number;
    locations: number;
    /** Monthly price per seat, in the location currency's minor unit. */
    p10: number;
    p25: number;
    p50: number;
    p75: number;
    p90: number;
}

export interface OfficePriceBenchmarks {
    currency: string;
    capacities: Record<TOfficeCapacityRange, OfficePriceBenchmark | null>;
    /** The most specific range over offices of every size, for comparing a
     *  location as a whole. Absent from older responses. */
    overall?: OfficePriceBenchmark | null;
}

export const MIN_SEATS = 1;
export const MAX_SEATS = 50;

/**
 * The lead's `desiredCapacity` for a seat count. The capacity ranges share
 * their edges ("1-3", "3-6"), so an edge goes to the lower range -- the same
 * thresholds as the backend's `capacityRangeFor`, which the price ranges are
 * built with.
 */
export const capacityForSeats = (seats: number): TOfficeCapacityRange => {
    if (seats <= 3) return "CAPACITY_1_3";
    if (seats <= 6) return "CAPACITY_3_6";
    if (seats <= 10) return "CAPACITY_6_10";
    if (seats <= 20) return "CAPACITY_10_20";
    return "CAPACITY_20_PLUS";
};

/**
 * A starting seat count for a capacity range picked earlier on the page: a
 * seat count inside it that maps straight back to the same range.
 */
export const seatsForCapacity = (capacity: string | undefined): number => {
    switch (capacity) {
        case "CAPACITY_1_3":
            return 2;
        case "CAPACITY_6_10":
            return 8;
        case "CAPACITY_10_20":
            return 15;
        case "CAPACITY_20_PLUS":
            return 25;
        case "CAPACITY_3_6":
        default:
            return 4;
    }
};
